import React, { useState, useEffect } from 'react';
import {
  Mail,
  Phone,
  Calendar,
  Pencil,
  Trash2,
  Check,
  CheckCircle2,
  X,
  AlertTriangle,
  Search,
  Lock,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useConversationStore } from '@/stores/useConversationStore';
import {
  useGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomer,
  useDeleteApiWorkspacesWorkspaceIdConversationsConversationIdCustomer,
  usePutApiWorkspacesWorkspaceIdConversationsConversationIdCustomerCustomerId,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsQueryKey,
} from '@/api/generated/conversations/conversations';
import {
  usePostApiWorkspacesWorkspaceIdCustomers,
  usePutApiWorkspacesWorkspaceIdCustomersCustomerId,
  useGetApiWorkspacesWorkspaceIdCustomers,
  getGetApiWorkspacesWorkspaceIdCustomersQueryKey,
} from '@/api/generated/customers/customers';
import { useQueryClient } from '@tanstack/react-query';
import type { CustomerDto, ConversationDetail } from '@/types';
import { getInitials, getCustomerDisplayName, cn } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { toast } from 'sonner';

interface CustomerDrawerProps {
  conversation: ConversationDetail | null;
}

export const CustomerDrawer: React.FC<CustomerDrawerProps> = ({ conversation }) => {
  const queryClient = useQueryClient();
  const { isCustomerDrawerOpen, setCustomerDrawerOpen } = useConversationStore();
  const { activeWorkspaceId } = useWorkspaceStore();

  const conversationId = conversation?.id || '';

  const statusStr = String(conversation?.status || '').toUpperCase();
  const isClosed = statusStr === 'CLOSED' || statusStr === 'RESOLVED' || conversation?.status === 3;

  const {
    data: customerData,
  } = useGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomer<CustomerDto>(
    activeWorkspaceId!,
    conversationId,
    {
      query: {
        enabled: Boolean(conversationId),
      },
    }
  );

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'create' | 'link'>('create');
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);

  // Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Search & Link Existing State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

  const { data: customersResponse } = useGetApiWorkspacesWorkspaceIdCustomers(
    activeWorkspaceId!,
    {
      StoreId: conversation?.storeId,
      SearchTerm: searchTerm || undefined,
    },
    {
      query: {
        enabled: isEditing && isCustomerDrawerOpen && activeTab === 'link' && !isClosed,
      },
    }
  );

  // Sync form state when customer or conversation changes
  useEffect(() => {
    if (customerData) {
      setName(customerData.name || conversation?.customerName || '');
      setEmail(customerData.email || conversation?.customerEmail || '');
      setPhone(customerData.phone || conversation?.customerPhone || '');
    } else if (conversation) {
      setName(conversation.customerName || '');
      setEmail(conversation.customerEmail || '');
      setPhone(conversation.customerPhone || '');
    }
  }, [customerData, conversation]);

  const createCustomerMutation = usePostApiWorkspacesWorkspaceIdCustomers();
  const updateCustomerMutation = usePutApiWorkspacesWorkspaceIdCustomersCustomerId();
  const linkCustomerMutation = usePutApiWorkspacesWorkspaceIdConversationsConversationIdCustomerCustomerId();
  const unlinkCustomerMutation = useDeleteApiWorkspacesWorkspaceIdConversationsConversationIdCustomer();

  const isSaving =
    createCustomerMutation.isPending ||
    updateCustomerMutation.isPending ||
    linkCustomerMutation.isPending;

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey(activeWorkspaceId!, conversationId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversationId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!),
    });
    queryClient.invalidateQueries({
      queryKey: getGetApiWorkspacesWorkspaceIdCustomersQueryKey(activeWorkspaceId!),
    });
  };

  const handleSaveCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversationId || isClosed) return;

    try {
      const existingCustomerId = customerData?.id || conversation?.customerId;

      if (existingCustomerId) {
        // Update existing customer record
        await updateCustomerMutation.mutateAsync({ workspaceId: activeWorkspaceId!,
          customerId: existingCustomerId,
          data: {
            name: name.trim() || undefined,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
          },
        });
        
        queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey(activeWorkspaceId!, conversationId), (old: any) => 
          old ? { 
            ...old, 
            name: name.trim() || old.name, 
            email: email.trim() || old.email, 
            phone: phone.trim() || old.phone 
          } : {
            id: existingCustomerId,
            name: name.trim() || undefined,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined
          }
        );

        queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversationId), (old: any) => 
          old ? { 
            ...old, 
            customerName: name.trim() || undefined,
            customerEmail: email.trim() || undefined,
            customerPhone: phone.trim() || undefined
          } : old
        );
        
        toast.success('Detalhes do cliente atualizados com sucesso');
      } else {
        // Create new customer and link to conversation
        const createdCustomer = await createCustomerMutation.mutateAsync({ workspaceId: activeWorkspaceId!,
          data: {
            storeId: conversation?.storeId || '',
            name: name.trim() || undefined,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
          },
        }) as any;

        const newCustomerId = createdCustomer?.id;
        if (newCustomerId) {
          await linkCustomerMutation.mutateAsync({ workspaceId: activeWorkspaceId!,
            conversationId,
            customerId: newCustomerId,
          });
          queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey(activeWorkspaceId!, conversationId), createdCustomer);
          queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversationId), (old: any) => 
            old ? { 
              ...old, 
              customerId: newCustomerId, 
              customerName: name.trim() || undefined,
              customerEmail: email.trim() || undefined,
              customerPhone: phone.trim() || undefined
            } : old
          );
        }
        toast.success('Perfil do cliente criado e vinculado à conversa');
      }

      invalidateAll();
      setIsEditing(false);
    } catch (err: any) {
      const msg = err.response?.data?.description || err.response?.data?.message || err.message || 'Falha ao salvar o cliente';
      toast.error(msg);
    }
  };

  const handleLinkExistingCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversationId || !selectedCustomerId || isClosed) return;

    try {
      await linkCustomerMutation.mutateAsync({ workspaceId: activeWorkspaceId!,
        conversationId,
        customerId: selectedCustomerId,
      });
      
      const linkedCustomer = rawCustomers.find((c: any) => c.id === selectedCustomerId || c.customerId === selectedCustomerId);
      if (linkedCustomer) {
        queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey(activeWorkspaceId!, conversationId), linkedCustomer);
        queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversationId), (old: any) => 
          old ? { 
            ...old, 
            customerId: selectedCustomerId, 
            customerName: linkedCustomer.name || linkedCustomer.customerName,
            customerEmail: linkedCustomer.email || linkedCustomer.customerEmail,
            customerPhone: linkedCustomer.phone || linkedCustomer.customerPhone
          } : old
        );
      }

      toast.success('Perfil do cliente vinculado à conversa');
      invalidateAll();
      setIsEditing(false);
      setSelectedCustomerId('');
      setSearchTerm('');
    } catch (err: any) {
      const msg = err.response?.data?.description || err.response?.data?.message || err.message || 'Falha ao vincular o cliente';
      toast.error(msg);
    }
  };

  const handleUnlink = async () => {
    if (!conversationId || isClosed) return;
    try {
      await unlinkCustomerMutation.mutateAsync({ workspaceId: activeWorkspaceId!, conversationId });
      
      // Clear cache immediately
      queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdCustomerQueryKey(activeWorkspaceId!, conversationId), null);
      queryClient.setQueryData(getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversationId), (old: any) => 
        old ? { ...old, customerId: null, customerName: null, customerEmail: null, customerPhone: null } : old
      );
      
      setName('');
      setEmail('');
      setPhone('');

      toast.success('Perfil do cliente desvinculado da conversa');
      invalidateAll();
      setIsEditing(false);
      setIsUnlinkConfirmOpen(false);
    } catch (err: any) {
      const msg = err.response?.data?.description || err.response?.data?.message || err.message || 'Falha ao desvincular o cliente';
      toast.error(msg);
    }
  };

  const isCustomerLinked = Boolean(
    (customerData?.id && customerData.name !== 'Unassigned Guest' && customerData.name !== 'Unassigned Customer') ||
      (conversation?.customerId) ||
      (conversation?.customerName &&
        conversation.customerName !== 'Unassigned Customer' &&
        conversation.customerName !== 'Unassigned Guest')
  );

  const activeName =
    name ||
    customerData?.name ||
    (customerData as any)?.customerName ||
    conversation?.customerName ||
    null;
  const activeEmail =
    email ||
    customerData?.email ||
    (customerData as any)?.customerEmail ||
    conversation?.customerEmail ||
    null;
  const activePhone =
    phone ||
    customerData?.phone ||
    (customerData as any)?.customerPhone ||
    conversation?.customerPhone;

  const displayCustomerName = getCustomerDisplayName(activeName, activeEmail);
  const initials = getInitials(activeName, activeEmail);

  // Normalize customer list from query response
  const rawCustomers = Array.isArray(customersResponse)
    ? customersResponse
    : (customersResponse as any)?.items || [];

  const filteredCustomers = rawCustomers.filter((c: any) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    const cName = (c.name || c.customerName || '').toLowerCase();
    const cEmail = (c.email || c.customerEmail || '').toLowerCase();
    return cName.includes(q) || cEmail.includes(q);
  });

  return (
    <>
      <Sheet open={isCustomerDrawerOpen} onOpenChange={setCustomerDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-6 space-y-6">
          <SheetHeader className="text-left space-y-1">
            <SheetTitle className="text-lg font-bold">Perfil do Cliente</SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              Visualize e gerencie detalhes de contato vinculados a este ticket de conversa.
            </SheetDescription>
          </SheetHeader>

          {/* Closed Conversation Lock Warning */}
          {isClosed && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted text-muted-foreground border border-border text-xs font-medium">
              <Lock className="h-4 w-4 shrink-0 text-amber-500" />
              <span>Esta conversa foi fechada e não pode ser modificada.</span>
            </div>
          )}

          {/* Profile Header Card */}
          <div className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
            <Avatar className="h-14 w-14 border border-border">
              <AvatarFallback className="bg-primary/10 text-primary text-base font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-base truncate text-foreground">{displayCustomerName}</h4>
              <p className="text-xs text-muted-foreground truncate">{activeEmail || 'Nenhum email vinculado'}</p>
              {conversation?.storeName && (
                <span className="inline-block mt-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground">
                  Loja: {conversation.storeName}
                </span>
              )}
            </div>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                disabled={isClosed}
                title={isClosed ? 'Conversa fechada' : 'Editar cliente'}
                className="h-8 gap-1.5 text-xs shrink-0"
              >
                <Pencil className="h-3.5 w-3.5" />
                <span>Editar</span>
              </Button>
            )}
          </div>

          {/* Details / Edit Form / Link Form */}
          {isEditing && !isClosed ? (
            <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-4">
              <Tabs
                defaultValue="create"
                value={activeTab}
                onValueChange={(val) => setActiveTab(val as 'create' | 'link')}
                className="w-full"
              >
                <div className="flex items-center justify-between mb-3">
                  <TabsList className="grid w-48 grid-cols-2 h-8">
                    <TabsTrigger value="create" className="text-xs px-2">
                      {isCustomerLinked ? 'Editar Detalhes' : 'Criar Novo'}
                    </TabsTrigger>
                    <TabsTrigger value="link" disabled={isClosed} className="text-xs px-2">
                      Vincular Existente
                    </TabsTrigger>
                  </TabsList>
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <TabsContent value="create" className="mt-0 space-y-4">
                  <form onSubmit={handleSaveCustomer} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="cust-name" className="text-xs font-medium">
                        Nome Completo
                      </Label>
                      <Input
                        id="cust-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="ex: Maria Silva"
                        className="text-xs h-8 bg-background"
                        disabled={isClosed}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cust-email" className="text-xs font-medium">
                        Endereço de Email
                      </Label>
                      <Input
                        id="cust-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ex: maria@exemplo.com"
                        className="text-xs h-8 bg-background"
                        disabled={isClosed}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cust-phone" className="text-xs font-medium">
                        Número de Telefone
                      </Label>
                      <Input
                        id="cust-phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="ex: +55 11 99999-9999"
                        className="text-xs h-8 bg-background"
                        disabled={isClosed}
                      />
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(false)}
                        className="h-8 text-xs"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={isSaving || isClosed}
                        className="h-8 text-xs gap-1.5"
                      >
                        <Check className="h-3.5 w-3.5" />
                        <span>{isSaving ? 'Salvando...' : 'Salvar Perfil'}</span>
                      </Button>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="link" className="mt-0 space-y-4">
                  <form onSubmit={handleLinkExistingCustomer} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="search-customer" className="text-xs font-medium">
                        Buscar Clientes Existentes
                      </Label>
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                          id="search-customer"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Buscar por nome ou email..."
                          className="text-xs h-8 pl-8 bg-background"
                          disabled={isClosed}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Selecionar Cliente</Label>
                      <div className="max-h-44 overflow-y-auto border border-border rounded-md bg-background divide-y divide-border/60">
                        {filteredCustomers.length === 0 ? (
                          <p className="p-3 text-xs text-muted-foreground text-center">
                            Nenhum cliente correspondente encontrado.
                          </p>
                        ) : (
                          filteredCustomers.map((c: any) => {
                            const cId = c.id || c.customerId;
                            const cName = c.name || c.customerName;
                            const cEmail = c.email || c.customerEmail;
                            const cDisplayName = getCustomerDisplayName(cName, cEmail);
                            const isSelected = selectedCustomerId === cId;
                            return (
                              <div
                                key={cId}
                                onClick={() => !isClosed && setSelectedCustomerId(cId)}
                                className={cn(
                                  "p-2.5 text-xs cursor-pointer flex items-center justify-between hover:bg-accent transition-colors",
                                  isSelected && "bg-primary/10 font-medium text-primary",
                                  isClosed && "cursor-not-allowed opacity-60 hover:bg-transparent"
                                )}
                              >
                                <div className="min-w-0 flex-1 pr-2">
                                  <span className="truncate block font-semibold">{cDisplayName}</span>
                                  <span className="text-[10px] text-muted-foreground truncate block">
                                    {cEmail || 'Nenhum email vinculado'}
                                  </span>
                                </div>
                                {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(false)}
                        className="h-8 text-xs"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={!selectedCustomerId || isSaving || isClosed}
                        className="h-8 text-xs gap-1.5"
                      >
                        <Check className="h-3.5 w-3.5" />
                        <span>{isSaving ? 'Vinculando...' : 'Vincular Cliente'}</span>
                      </Button>
                    </div>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border/70 text-xs">
                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] text-muted-foreground block">Email</span>
                  <span className="font-medium text-foreground truncate block">
                    {activeEmail || 'Não especificado'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border/70 text-xs">
                <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] text-muted-foreground block">Telefone</span>
                  <span className="font-medium text-foreground truncate block">
                    {activePhone || 'Não especificado'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border/70 text-xs">
                <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] text-muted-foreground block">Conversa Iniciada</span>
                  <span className="font-medium text-foreground truncate block">
                    {conversation?.createdAtUtc || conversation?.startedAt
                      ? new Date(conversation.createdAtUtc || conversation.startedAt!).toLocaleString('pt-BR')
                      : 'N/D'}
                  </span>
                </div>
              </div>

              {conversation?.closedAtUtc && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border/70 text-xs">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] text-muted-foreground block">Conversa Encerrada</span>
                    <span className="font-medium text-foreground truncate block">
                      {new Date(conversation.closedAtUtc).toLocaleString('pt-BR')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Danger Zone: Unlink Customer - Only visible when customer is linked */}
          {isCustomerLinked && (
            <>
              <Separator />
              <div className="space-y-3 p-4 rounded-xl border border-destructive/20 bg-destructive/5">
                <div className="flex items-center gap-2 text-destructive font-semibold text-xs">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Zona de Perigo</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  A desvinculação removerá a associação do perfil do cliente desta conversa.
                </p>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setIsUnlinkConfirmOpen(true)}
                  disabled={unlinkCustomerMutation.isPending || isClosed}
                  className="w-full text-xs gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span>Desvincular Perfil do Cliente</span>
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Unlink Customer Confirmation Dialog */}
      <ConfirmDialog
        open={isUnlinkConfirmOpen}
        onOpenChange={setIsUnlinkConfirmOpen}
        title="Desvincular Perfil do Cliente"
        description="Tem certeza que deseja remover a associação do cliente? Os detalhes de contato serão desvinculados."
        confirmLabel="Desvincular Cliente"
        variant="destructive"
        onConfirm={handleUnlink}
      />
    </>
  );
};

export default CustomerDrawer;
